import { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Snackbar } from "@material-ui/core";

import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";
import { IsMobile } from "utils/is-mobile";

import { DialogProductDetailShowcaseClient } from "./components/product-detail";
import BrandSelector from "./components/brand-selector";
import VitrineHeader from "./components/vitrine-header";
import ProductCard from "./components/product-card";
import CartSidebar from "./components/cart-sidebar";
import CartButton from "./components/cart-button";
import SpecsModal from "./components/specs-modal";
import ConfirmationModal from "./components/confirmation-modal";
import PersonalDataForm from "./components/personal-data-form";
import ErrorMessages from "./components/error-messages";
import useCartManager from "./hooks/useCartManager";
import themeColorsDesingSystem from "assets/theme/ds-colors";

const getBrandColors = (selectedBrand) => {
  const karsten = themeColorsDesingSystem.brands.karsten;
  const trussardi = themeColorsDesingSystem.brands.trussardi;

  switch (selectedBrand) {
    case "trussardi":
      return {
        primary: trussardi.primary,
        primaryHover: trussardi.primaryHover,
        bg: trussardi.bg,
        surface: trussardi.surface,
        textBase: trussardi.textBase,
        textMuted: trussardi.textMuted,
        border: trussardi.border,
      };
    case "karsten-decor":
      return {
        primary: karsten.primary,
        primaryHover: karsten.primaryHover,
        bg: karsten.bg,
        surface: karsten.surface,
        textBase: karsten.grafite,
        textMuted: karsten.cinza,
        border: karsten.areia,
      };
    case "mix":
      return {
        primary: "#222222",
        primaryHover: "#333333",
        bg: karsten.bg,
        surface: karsten.surface,
        textBase: "#222222",
        textMuted: "#777777",
        border: karsten.areia,
        karstenAccent: karsten.accent,
        trussardiAccent: trussardi.accent,
      };
    case "karsten":
    default:
      return {
        primary: karsten.primary,
        primaryHover: karsten.primaryHover,
        bg: karsten.bg,
        surface: karsten.surface,
        textBase: karsten.grafite,
        textMuted: karsten.cinza,
        border: karsten.areia,
      };
  }
};

function setProductsToStorage(hash, products) {
  localStorage.setItem("vitrine_products", JSON.stringify({ hash, products }));

  const allProductsData = localStorage.getItem("vitrine_all_products");
  const allProducts = allProductsData ? JSON.parse(allProductsData) : {};

  products.forEach((product) => {
    allProducts[product.idProduto] = product;
  });

  localStorage.setItem("vitrine_all_products", JSON.stringify(allProducts));
}

function getAllProductsFromStorage() {
  try {
    const allProductsData = localStorage.getItem("vitrine_all_products");
    return allProductsData ? JSON.parse(allProductsData) : {};
  } catch (error) {
    return {};
  }
}

function clearProductsStorage() {
  localStorage.removeItem("vitrine_products");
}

const useStyles = makeStyles(() => ({
  karstenLayout: {
    "&": {
      "--karsten-azul": "#005870",
      "--karsten-turquesa": "#92CFCA",
      "--karsten-grafite": "#3C3C3B",
      "--karsten-areia": "#D4D3D3",
      "--karsten-cinza": "#878787",
      "--karsten-bg": "#F4F4F4",
      "--karsten-surface": "#FFFFFF",
    },
    "& .bg-brand": {
      backgroundColor: "#005870",
    },
    "& .hover\\:bg-brand-hover:hover": {
      backgroundColor: "#004a5e",
    },
    "& .text-brand": {
      color: "#005870",
    },
    "& .text-accent": {
      color: "#92CFCA",
    },
    "& .border-brand": {
      borderColor: "#005870",
    },
    "& .font-display": {
      fontFamily: "'Lora', serif",
    },
  },
  trussardiLayout: {
    "&": {
      "--trussardi-bg": "#FFFFFF",
      "--trussardi-surface": "#FFFFFF",
      "--trussardi-primary": "#B7A99A",
      "--trussardi-primary-hover": "#A98B6C",
      "--trussardi-text-base": "#000000",
      "--trussardi-text-muted": "#6B6B6B",
      "--trussardi-border": "#EAEAEA",
    },
    "& .bg-brand": {
      backgroundColor: "#B7A99A",
    },
    "& .hover\\:bg-brand-hover:hover": {
      backgroundColor: "#A98B6C",
    },
    "& .text-brand": {
      color: "#B7A99A",
    },
    "& .surface": {
      backgroundColor: "#FFFFFF",
    },
    "& .text-muted": {
      color: "#6B6B6B",
    },
    "& .border-color": {
      borderColor: "#EAEAEA",
    },
    "& .font-title": {
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 500,
    },
  },
  mixLayout: {
    "&": {
      "--mix-bg": "#F8F8F8",
      "--mix-surface": "#FFFFFF",
      "--mix-text-base": "#222222",
      "--mix-text-muted": "#777777",
      "--mix-border": "#E0E0E0",
      "--mix-karsten-accent": "#005870",
      "--mix-trussardi-accent": "#B7A99A",
    },
    "& .surface": {
      backgroundColor: "#FFFFFF",
    },
    "& .text-muted": {
      color: "#777777",
    },
    "& .border-color": {
      borderColor: "#E0E0E0",
    },
  },
  cartClosed: {
    transform: "translateX(100%)",
  },
  cartOpen: {
    transform: "translateX(0)",
  },
  scrollHidden: {
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  imageCarousel: {
    "&::-webkit-scrollbar": {
      display: "none",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  numberInput: {
    "&::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    "&::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    MozAppearance: "textfield",
  },
}));

export default function VitrineVirtualClientes() {
  const classes = useStyles();
  const mobile = IsMobile();

  const [data, setData] = useState(null);
  const {
    cart,
    addToCart: addToCartManager,
    removeFromCart: removeFromCartManager,
    updateCartQuantity: updateCartQuantityManager,
    clearCart,
    getTotalItems,
    validateCartItems,
  } = useCartManager(data?.hashVitrine);
  const [openDetail, setOpenDetail] = useState(false);
  const [productDetail, setProductDetail] = useState(null);
  const [searching, setSearching] = useState(false);
  const [openMessageError, setOpenMessageError] = useState(false);
  const [finishOrder, setFinishOrder] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [personalData, setPersonalData] = useState(false);
  const [cpfInput, setCpfInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [birthDateInput, setBirthDateInput] = useState("");
  const [deliveryAddressExpanded, setDeliveryAddressExpanded] = useState(false);
  const [deliveryCep, setDeliveryCep] = useState("");
  const [deliveryStreet, setDeliveryStreet] = useState("");
  const [deliveryNumber, setDeliveryNumber] = useState("");
  const [deliveryNeighborhood, setDeliveryNeighborhood] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [deliveryUf, setDeliveryUf] = useState("");
  const [deliveryComplement, setDeliveryComplement] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [personalDataError, setPersonalDataError] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("karsten");
  const [availableBrands, setAvailableBrands] = useState([]);
  const [openSpecsModal, setOpenSpecsModal] = useState(false);
  const [specsProduct, setSpecsProduct] = useState(null);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [productQuantities, setProductQuantities] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const processApiResponse = (response) => {
    const hash = response.Result?.HashVitrine;
    const showcase = response.Result?.VitrineVirtualConsumidorEnvio;
    const products = (showcase?.Itens || []).map((item) => {
      const listaRaw = item?.EspecificacoesLista ?? item?.especificacoesLista;
      const especificacoesLista = Array.isArray(listaRaw)
        ? listaRaw.map((e) => ({
            idEspecificacao: e?.IdEspecificacao ?? e?.idEspecificacao,
            descricao: e?.Descricao ?? e?.descricao ?? "",
            valor1: e?.Valor1 ?? e?.valor1 ?? "",
            valor2: e?.Valor2 ?? e?.valor2 ?? "",
            valor: e?.Valor ?? e?.valor ?? "",
            aplicacaoEspecificacao:
              e?.AplicacaoEspecificacao ?? e?.aplicacaoEspecificacao ?? null,
          }))
        : [];

      const especFromApi = (
        item?.Especificacoes ??
        item?.especificacoes ??
        ""
      ).trim();

      const precoAnterior =
        item?.PrecoPromo === 0 || item?.PrecoPromo === item?.Preco
          ? null
          : item?.PrecoFormat;
      const preco =
        item?.PrecoPromo === 0 || item?.PrecoPromo === item?.Preco
          ? item?.PrecoFormat
          : item?.PrecoPromoFormat;
      const precoAnteriorNum = precoAnterior
        ? parseFloat(precoAnterior.replace(/[^\d,]/g, "").replace(",", "."))
        : null;
      const precoNum = parseFloat(
        preco.replace(/[^\d,]/g, "").replace(",", ".")
      );
      const desconto =
        precoAnteriorNum && precoNum && precoAnteriorNum > precoNum
          ? Math.round(((precoAnteriorNum - precoNum) / precoAnteriorNum) * 100)
          : null;

      return {
        fotos: item?.Fotos ?? item?.fotos ?? [],
        descricao: item?.DescricaoProduto || "",
        idProduto: item?.IdProduto?.toString() || "",
        name: item?.DescricaoProduto || "",
        precoAnterior: precoAnterior,
        preco: preco,
        desconto: desconto,
        codigo: item?.IdProduto?.toString() || "",
        unidadeMedida: item?.UnidadeMedida || "UN",
        marcaId: item?.IdMarca ?? item?.idMarca,
        especificacoesLista,
        specs:
          especFromApi ||
          item?.DescricaoProduto ||
          item?.descricaoProduto ||
          "",
      };
    });

    return {
      id: showcase?.IdVitrineVirtual,
      hashVitrine: hash,
      products,
      nameVitrine: showcase?.NomeVitrine || showcase?.Nome || "",
      store: showcase?.Loja || "",
      vendedor: showcase?.Vendedor || "",
      idVendedor: showcase?.IdVendedor || response.Result?.IdLojaVendedor,
      storeId: showcase?.IdPessoaLoja || response.Result?.IdPessoaLoja,
      brandId: showcase?.IdMarcaKarsten,
      isExclusive: !showcase?.VitrinePublica,
      nameConsumidor: showcase?.NomeConsumidor || "",
      cpf: showcase?.Cpf || "",
    };
  };

  const handleGetClientShowcase = async (client) => {
    setSearching(true);
    setOpenMessageError(false);

    try {
      const response = await Api.get(
        "virtual-customer-detail",
        "showcase",
        `?hashVitrine=${client}`,
        false
      );
      const processedData = processApiResponse(response);
      setData(processedData);
    } catch (error) {
      setOpenMessageError(true);
    } finally {
      setSearching(false);
    }
  };

  const getHashFromUrl = () => {
    const searchParams = new URLSearchParams(window.location.search);

    let hashVitrine = searchParams.get("hashVitrine");

    if (!hashVitrine) {
      const queryString = window.location.search.substring(1);
      const params = queryString.split("&");

      for (const param of params) {
        if (!param) continue;

        if (!param.includes("=")) {
          hashVitrine = param;
          break;
        }

        const [key, value] = param.split("=");
        if (key && key !== "cpf" && value) {
          hashVitrine = value;
          break;
        }
      }
    }

    return hashVitrine;
  };

  useEffect(() => {
    const hashVitrine = getHashFromUrl();
    if (hashVitrine) {
      handleGetClientShowcase(hashVitrine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const brandIds = new Set();

    if (data && data.products && data.products.length > 0) {
      data.products.forEach((product) => {
        if (product.marcaId) {
          brandIds.add(product.marcaId);
        }
      });
    }

    const hasKarsten = brandIds.has(1) || brandIds.has(3);
    const hasTrussardi = brandIds.has(2);
    const hasMultipleBrands = hasKarsten && hasTrussardi;

    const available = [
      {
        id: 1,
        name: "karsten",
        label: "Karsten",
        disabled: !hasKarsten,
      },
      {
        id: 2,
        name: "trussardi",
        label: "Trussardi",
        disabled: !hasTrussardi,
      },
      {
        id: "mix",
        name: "mix",
        label: "Mix",
        disabled: !hasMultipleBrands,
      },
    ];

    setAvailableBrands(available);

    if (available.length > 0) {
      const currentBrandId =
        selectedBrand === "karsten"
          ? 1
          : selectedBrand === "trussardi"
          ? 2
          : selectedBrand === "mix"
          ? "mix"
          : null;
      const currentBrand = available.find((b) => b.id === currentBrandId);
      const isCurrentBrandAvailable = currentBrand && !currentBrand.disabled;

      if (!isCurrentBrandAvailable) {
        const firstEnabledBrand = available.find((b) => !b.disabled);
        if (firstEnabledBrand) {
          setSelectedBrand(firstEnabledBrand.name);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.products, selectedBrand]);

  useEffect(() => {
    if (!data || !data.products) return;
    setProductsToStorage(data.id, data.products);

    const allProducts = getAllProductsFromStorage();
    const allProductsArray = Object.values(allProducts);

    if (allProductsArray.length > 0) {
      validateCartItems(allProductsArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.hashVitrine]);

  useEffect(() => {
    if (finishOrder === "success") {
      clearCart();
      clearProductsStorage();
      setOpenConfirmationModal(true);
    }
  }, [finishOrder, clearCart]);

  useEffect(() => {
    if (isCartOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isCartOpen]);

  useEffect(() => {
    if (!selectedBrand) return;

    const brandColors = getBrandColors(selectedBrand);
    const bgColor = brandColors.bg;
    document.body.style.backgroundColor = bgColor;
    document.documentElement.style.backgroundColor = bgColor;

    return () => {
      document.body.style.backgroundColor = "";
      document.documentElement.style.backgroundColor = "";
    };
  }, [selectedBrand]);

  function getCartDetails() {
    if (!data) return [];

    const allProducts = getAllProductsFromStorage();

    return cart
      .map((item) => {
        let prod = data.products.find((p) => p.idProduto === item.idProduto);

        if (!prod && allProducts[item.idProduto]) {
          prod = allProducts[item.idProduto];
        }

        return prod ? { ...prod, quantidade: item.quantidade } : null;
      })
      .filter(Boolean);
  }

  const addToCart = (idProduto, quantidade = 1) => {
    const product = data?.products?.find((p) => p.idProduto === idProduto);
    const productName = product?.name || "Item";

    addToCartManager(idProduto, quantidade);
    setProductQuantities((prev) => ({ ...prev, [idProduto]: 0 }));

    setSnackbarMessage(`Item "${productName}" adicionado ao carrinho`);
    setSnackbarOpen(true);
  };

  const removeFromCart = (idProduto) => {
    removeFromCartManager(idProduto);
  };

  const updateCartQuantity = (idProduto, quantidade) => {
    updateCartQuantityManager(idProduto, quantidade);
  };

  function getPersonalDataValidationError() {
    const name = (nameInput || data?.nameConsumidor || "").trim();
    if (name.length < 2) {
      return {
        message: "Informe seu nome com pelo menos 2 caracteres.",
        expandAddress: false,
      };
    }
    const cpfDigits = (cpfInput || data?.cpf || "").replace(/[^\d]+/g, "");
    if (!MaskAndValid.validateCPF(cpfDigits)) {
      return {
        message: "CPF inválido. Verifique os dígitos informados.",
        expandAddress: false,
      };
    }
    if (!birthDateInput || !MaskAndValid.validateDate(birthDateInput)) {
      return {
        message: "Data de nascimento inválida. Selecione uma data válida.",
        expandAddress: false,
      };
    }
    const phoneDigits = phoneInput.replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      return {
        message:
          "Telefone inválido. Informe DDD + número (10 ou 11 dígitos).",
        expandAddress: false,
      };
    }
    if (!MaskAndValid.validateEmail(emailInput.trim())) {
      return {
        message: "E-mail inválido. Verifique o formato.",
        expandAddress: false,
      };
    }
    const cepDigits = deliveryCep.replace(/\D/g, "");
    if (cepDigits.length !== 8) {
      return {
        message: "CEP inválido. Informe os 8 dígitos do CEP.",
        expandAddress: true,
      };
    }
    if (!deliveryStreet.trim()) {
      return { message: "Logradouro é obrigatório.", expandAddress: true };
    }
    if (!deliveryNumber.trim()) {
      return { message: "Número é obrigatório.", expandAddress: true };
    }
    if (!deliveryNeighborhood.trim()) {
      return { message: "Bairro é obrigatório.", expandAddress: true };
    }
    if (!deliveryCity.trim()) {
      return { message: "Cidade é obrigatória.", expandAddress: true };
    }
    const uf = deliveryUf.trim().toUpperCase();
    if (uf.length !== 2 || !/^[A-Z]{2}$/.test(uf)) {
      return {
        message: "UF inválida. Informe a sigla com 2 letras.",
        expandAddress: true,
      };
    }
    return null;
  }

  function isPersonalDataComplete() {
    return getPersonalDataValidationError() === null;
  }

  const fetchDeliveryAddressByCep = async (cepDigits) => {
    if (!cepDigits || cepDigits.length !== 8) return;
    setCepLoading(true);
    try {
      const response = await Api.get(
        "get-address",
        "addressLookup",
        `?cep=${cepDigits}`,
        true
      );
      const raw =
        response?.Result ??
        response?.result ??
        response?.Data ??
        response?.data ??
        {};
      const logradouro =
        raw.Logradouro ??
        raw.logradouro ??
        raw.Endereco ??
        raw.endereco ??
        "";
      const bairro = raw.Bairro ?? raw.bairro ?? "";
      const localidade =
        raw.Localidade ??
        raw.localidade ??
        raw.Cidade ??
        raw.cidade ??
        raw.Municipio ??
        raw.municipio ??
        "";
      const ufRaw = raw.Uf ?? raw.UF ?? raw.uf ?? "";
      setDeliveryStreet(String(logradouro));
      setDeliveryNeighborhood(String(bairro));
      setDeliveryCity(String(localidade));
      setDeliveryUf(
        ufRaw
          ? String(ufRaw)
              .replace(/[^a-zA-Z]/g, "")
              .slice(0, 2)
              .toUpperCase()
          : ""
      );
    } catch (error) {
      setSnackbarMessage(
        "Não foi possível buscar o endereço para este CEP. Verifique e tente novamente."
      );
      setSnackbarOpen(true);
    } finally {
      setCepLoading(false);
    }
  };

  const handleFinishOrder = async () => {
    setIsCartOpen(false);

    if (!isPersonalDataComplete()) {
      setPersonalData(true);
      if (data?.cpf) {
        setCpfInput(
          MaskAndValid.maskCPF(String(data.cpf).replace(/\D/g, ""))
        );
      }
      if (data?.nameConsumidor) {
        setNameInput(data.nameConsumidor);
      }
      return;
    }

    const nameConsumidor = (nameInput || data?.nameConsumidor || "").trim();
    const cpf = (cpfInput || data?.cpf || "").replace(/[^\d]+/g, "");
    const telefoneDigits = phoneInput.replace(/\D/g, "");

    const params = {
      tokenVitrine: data?.hashVitrine,
      idLojaVendedor: data?.idVendedor,
      IdPessoaLoja: data?.storeId,
      nomeConsumidor: nameConsumidor,
      cpf: cpf,
      email: emailInput.trim(),
      telefone: telefoneDigits,
      dataNascimento: birthDateInput,
      enderecoEntrega: {
        cep: deliveryCep.replace(/\D/g, ""),
        logradouro: deliveryStreet.trim(),
        numero: deliveryNumber.trim(),
        bairro: deliveryNeighborhood.trim(),
        cidade: deliveryCity.trim(),
        uf: deliveryUf.trim().toUpperCase(),
        complemento: deliveryComplement.trim() || "",
      },
      jsonProdutos: cart,
      statusCarrinho: "CONFIRMADO",
    };

    try {
      await Api.post(
        "edit-item-virtual-customer",
        "showcase",
        {
          jsonVitrine: params,
        },
        true
      );
      setFinishOrder("success");
    } catch (error) {
      setFinishOrder("error");
    }
  };

  const handlePersonalDataSubmit = () => {
    const err = getPersonalDataValidationError();
    if (err) {
      setPersonalDataError(err.message);
      if (err.expandAddress) {
        setDeliveryAddressExpanded(true);
      }
      return;
    }
    setPersonalData(false);
    setPersonalDataError("");
    setTimeout(() => {
      handleFinishOrder();
    }, 0);
  };

  const brandColors = getBrandColors(selectedBrand);

  const handleQuantityChange = (productId, delta) => {
    setProductQuantities((prev) => {
      const current = prev[productId] || 1;
      const newValue = Math.max(0, current + delta);
      return { ...prev, [productId]: newValue };
    });
  };

  const handleCarouselScroll = (productId, direction) => {
    const carousel = document.querySelector(
      `[data-product-id="${productId}"] [data-carousel="true"]`
    );
    if (carousel) {
      const scrollAmount =
        direction === "next" ? carousel.clientWidth : -carousel.clientWidth;
      carousel.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  const handleShowSpecs = (product) => {
    setSpecsProduct(product);
    setOpenSpecsModal(true);
  };

  const cartDetails = getCartDetails();
  const totalItems = getTotalItems();

  let subtotal = 0;
  let total = 0;
  let discount = 0;

  cartDetails.forEach((item) => {
    const precoAnterior = item.precoAnterior
      ? parseFloat(item.precoAnterior.replace(",", "."))
      : parseFloat(item.preco.replace(",", "."));
    const preco = parseFloat(item.preco.replace(",", "."));
    subtotal += precoAnterior * item.quantidade;
    total += preco * item.quantidade;
  });
  discount = subtotal - total;

  const isKarstenLayout = selectedBrand === "karsten";
  const isTrussardiLayout = selectedBrand === "trussardi";

  return (
    <div
      className={
        isKarstenLayout
          ? classes.karstenLayout
          : isTrussardiLayout
          ? classes.trussardiLayout
          : selectedBrand === "mix"
          ? classes.mixLayout
          : classes.karstenLayout
      }
      style={{
        position: "relative",
        minHeight: "100vh",
        display: mobile ? "block" : "flex",
        backgroundColor: brandColors.bg,
        color: brandColors.textBase,
        fontFamily: "'Alexandria', sans-serif",
        ...(mobile && {
          paddingBottom: "120px",
        }),
      }}
    >
      <BrandSelector
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        availableBrands={availableBrands}
        mobile={mobile}
      />

      <main
        className="main-content"
        style={{
          width: "100%",
          padding: mobile ? "16px" : "32px",
        }}
      >
        <VitrineHeader
          selectedBrand={searching ? "mix" : selectedBrand}
          brandColors={brandColors}
          data={data}
          mobile={mobile}
        />

        <ErrorMessages
          openMessageError={openMessageError}
          finishOrder={finishOrder}
        />

        {!finishOrder && !openMessageError && (
          <>
            <PersonalDataForm
              open={personalData}
              onClose={() => setPersonalData(false)}
              nameInput={nameInput}
              setNameInput={setNameInput}
              cpfInput={cpfInput}
              setCpfInput={setCpfInput}
              phoneInput={phoneInput}
              setPhoneInput={setPhoneInput}
              emailInput={emailInput}
              setEmailInput={setEmailInput}
              birthDateInput={birthDateInput}
              setBirthDateInput={setBirthDateInput}
              deliveryAddressExpanded={deliveryAddressExpanded}
              setDeliveryAddressExpanded={setDeliveryAddressExpanded}
              deliveryCep={deliveryCep}
              setDeliveryCep={setDeliveryCep}
              deliveryStreet={deliveryStreet}
              setDeliveryStreet={setDeliveryStreet}
              deliveryNumber={deliveryNumber}
              setDeliveryNumber={setDeliveryNumber}
              deliveryNeighborhood={deliveryNeighborhood}
              setDeliveryNeighborhood={setDeliveryNeighborhood}
              deliveryCity={deliveryCity}
              setDeliveryCity={setDeliveryCity}
              deliveryUf={deliveryUf}
              setDeliveryUf={setDeliveryUf}
              deliveryComplement={deliveryComplement}
              setDeliveryComplement={setDeliveryComplement}
              cepLoading={cepLoading}
              fetchDeliveryAddressByCep={fetchDeliveryAddressByCep}
              error={personalDataError}
              onSubmit={handlePersonalDataSubmit}
              brandColors={brandColors}
            />
            {!personalData && searching ? (
              <div style={{ textAlign: "center", padding: "24px" }}>
                <p>Buscando informações...</p>
                <div
                  style={{
                    width: "100%",
                    height: "4px",
                    backgroundColor: brandColors.border,
                    borderRadius: "2px",
                    marginTop: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "30%",
                      height: "100%",
                      backgroundColor: brandColors.primary,
                      animation: "pulse 1.5s ease-in-out infinite",
                    }}
                  />
                </div>
              </div>
            ) : !data || !data.products || data.products.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "48px 24px",
                  color: brandColors.textMuted,
                }}
              >
                <p style={{ fontSize: mobile ? "16px" : "18px", margin: 0 }}>
                  Nenhum produto disponível no momento.
                </p>
              </div>
            ) : (
              <div
                className="product-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: mobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fill, minmax(250px, 1fr))",
                  gap: mobile ? "16px" : "24px",
                }}
              >
                {data.products
                  .filter((product) => {
                    if (selectedBrand === "mix") {
                      return true;
                    }

                    if (selectedBrand === "karsten") {
                      return product.marcaId === 1 || product.marcaId === 3;
                    }

                    if (selectedBrand === "trussardi") {
                      return product.marcaId === 2;
                    }
                    return true;
                  })
                  .map((product) => (
                    <ProductCard
                      key={product.idProduto}
                      product={product}
                      brandColors={brandColors}
                      selectedBrand={selectedBrand}
                      quantity={productQuantities[product.idProduto] || 1}
                      onQuantityChange={handleQuantityChange}
                      onAddToCart={addToCart}
                      onShowSpecs={handleShowSpecs}
                      onCarouselScroll={handleCarouselScroll}
                    />
                  ))}
              </div>
            )}
          </>
        )}
      </main>

      <CartSidebar
        isCartOpen={isCartOpen}
        setIsCartOpen={setIsCartOpen}
        cartDetails={cartDetails}
        brandColors={brandColors}
        selectedBrand={selectedBrand}
        subtotal={subtotal}
        total={total}
        discount={discount}
        onRemoveFromCart={removeFromCart}
        onUpdateCartQuantity={updateCartQuantity}
        onFinishOrder={handleFinishOrder}
      />

      <CartButton
        onClick={() => setIsCartOpen(!isCartOpen)}
        totalItems={totalItems}
        brandColors={brandColors}
      />

      <SpecsModal
        open={openSpecsModal}
        product={specsProduct}
        onClose={() => setOpenSpecsModal(false)}
        brandColors={brandColors}
        selectedBrand={selectedBrand}
      />

      <ConfirmationModal
        open={openConfirmationModal}
        onClose={() => {
          setOpenConfirmationModal(false);
          setIsCartOpen(false);
        }}
        brandColors={brandColors}
      />

      {openDetail && (
        <DialogProductDetailShowcaseClient
          storeId={data?.storeId}
          detail={productDetail}
          open={openDetail}
          handleClose={() => {
            setProductDetail(null);
            setOpenDetail(false);
          }}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        style={{
          bottom: mobile ? "100px" : "120px",
        }}
      />
    </div>
  );
}
